/**
 * EvidenceDrawer - Slide-over panel for evidence details with inline review controls.
 * Shows: Store info, all postings with photos, verification context, issues.
 * Supports per-task approve/reject both in the drawer and inside the photo lightbox.
 */

import DOMPurify from "dompurify";
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getJson, apiFetch } from "@/lib/http";
import { acknowledgeEvidenceReview } from "@/lib/evidenceReviewCache";
import { scheduleReviewRefresh } from "@/lib/reviewDecisionRefresh";
import { trackerApiQueryString } from "@/lib/trackerTaskDefinitionFilter";
import { TaskSubmissionNotes } from "./TaskSubmissionNotes";
import {
    Drawer,
    DrawerContent,
    DrawerHeader,
    DrawerTitle,
    DrawerDescription,
    DrawerFooter,
    DrawerClose
} from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { NewFeatureBadge } from "@/components/tracker/NewFeatureBadge";
import { REJECTION_TYPE_DISPLAY_ORDER, type RejectionType } from "@shared/rejection-types";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { format, formatDistanceToNow } from "date-fns";
import {
    Camera, X, Package, Trash2, AlertTriangle, PenLine,
    CheckCircle2, ThumbsUp, ThumbsDown, ChevronDown, ChevronUp,
    CheckCheck, Clock, XCircle, ChevronLeft, ChevronRight, Edit3, Save, Undo2, ZoomIn, ZoomOut,
    ShieldCheck, Upload, Check, RotateCcw
} from "lucide-react";

import { FormatStatusTable } from "@/components/shared/FormatStatusTable";
import { RemovalSourcePostingReferences } from "@/components/proof-of-posting/RemovalSourcePostingReferences";
import { CurrentSubmissionEvidencePanel } from "@/components/proof-of-posting/CurrentSubmissionEvidencePanel";
import { getTaskTypeActionLabel, getReviewDecisionScopeHint, getReferenceKindGroupCount } from "@/components/proof-of-posting/submissionReferenceLabels";
import type { TrackerSourcePostingReference } from "@shared/tracker-types";
import type {
    TrackerEvidenceResponse,
    TrackerManagerSignature,
    TrackerTask,
    TrackerMediaFormat,
    TrackerReviewStatus
} from "@shared/tracker-types";
import {
    buildTrackerReviewFilterCounts,
    filterTrackerTasksByReview,
    isTrackerTaskReviewable,
    type TrackerReviewFilter,
} from "@shared/tracker-review-eligibility";
import { formatTrackerVisitDateTime } from "./dateFormatting";
import { isReviewPresenceEnabled } from "@/lib/reviewPresenceFlag";
import { useReviewPresenceLifecycle } from "@/hooks/useReviewPresenceLifecycle";

interface EvidenceDrawerProps {
    cycleId: number;
    routeItemId: string;
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

class ReviewDecisionError extends Error {
    repostTaskId?: number | null;

    constructor(message: string, options?: { repostTaskId?: number | null }) {
        super(message);
        this.name = "ReviewDecisionError";
        this.repostTaskId = options?.repostTaskId ?? null;
    }
}

type ReviewErrorState = {
    message: string;
    repostTaskId?: number | null;
};

function formatReviewDecisionError(errorData: any, fallback: string) {
    const errorCode = typeof errorData?.errorCode === "string" ? errorData.errorCode : undefined;
    const repostTaskId = typeof errorData?.repostTaskId === "number" ? errorData.repostTaskId : null;
    if (errorCode === "REPOST_IN_PROGRESS") {
        const taskLabel = repostTaskId ? ` #${repostTaskId}` : "";
        return {
            message: `This original submission cannot be approved because repost task${taskLabel} is already in progress. Open/review or cancel that repost task first, then retry approving the original.`,
            repostTaskId,
        };
    }

    return {
        message: typeof errorData?.error === "string" && errorData.error.trim().length > 0 ? errorData.error : fallback,
        repostTaskId,
    };
}

// ─── Flat photo list entry (used by lightbox) ──────────────────────────────────

interface FlatPhoto {
    photoId: string;
    fullUrl: string;
    thumbUrl: string;
    taskIndex: number;       // index into allTasks
    photoIndex: number;      // index within this task's photos
    globalIndex: number;     // position in the flattened list
}

function buildFlatPhotos(tasks: TrackerTask[]): FlatPhoto[] {
    const flat: FlatPhoto[] = [];
    tasks.forEach((task, taskIndex) => {
        task.photos.forEach((photo, photoIndex) => {
            flat.push({
                photoId: photo.photoId,
                fullUrl: photo.fullUrl,
                thumbUrl: photo.thumbUrl,
                taskIndex,
                photoIndex,
                globalIndex: flat.length
            });
        });
    });
    return flat;
}

// ─── Review status badge ───────────────────────────────────────────────────────

function ReviewBadge({ status, reviewable }: { status: TrackerReviewStatus; reviewable: boolean }) {
    if (!reviewable) {
        return (
            <span className="inline-flex items-center gap-1 text-xs text-muted-foreground bg-muted border border-border rounded-full px-2 py-0.5">
                <Clock className="h-3 w-3" /> Awaiting submission
            </span>
        );
    }

    switch (status) {
        case "approved":
            return (
                <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded-full px-2 py-0.5">
                    <CheckCircle2 className="h-3 w-3" /> Approved
                </span>
            );
        case "rejected":
            return (
                <span className="inline-flex items-center gap-1 text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded-full px-2 py-0.5">
                    <XCircle className="h-3 w-3" /> Rejected
                </span>
            );
        case "pending":
        default:
            return (
                <span className="inline-flex items-center gap-1 text-xs text-muted-foreground bg-muted border border-border rounded-full px-2 py-0.5">
                    <Clock className="h-3 w-3" /> Not reviewed
                </span>
            );
    }
}

// ─── Task status badge ─────────────────────────────────────────────────────────

function getTaskStatusBadge(status: TrackerTask['status']) {
    switch (status) {
        case 'complete':
            return <Badge variant="success" className="text-xs">Complete</Badge>;
        case 'partial':
            return <Badge variant="warning" className="text-xs">Partial</Badge>;
        case 'issue':
            return <Badge variant="destructive" className="text-xs">Issue</Badge>;
        case 'pending':
        default:
            return <Badge variant="outline" className="text-xs">Pending</Badge>;
    }
}

// ─── Reject form (inline, expandable) ─────────────────────────────────────────

const EVIDENCE_DRAWER_REJECTION_LABELS: Record<RejectionType, string> = {
    ADVISORY_ONLY: "Advisory only",
    PHOTO_ONLY: "Photo retake only",
    REVISIT: "Revisit required",
    REVISIT_WITH_SIGNOFF: "Revisit + manager sign-off",
};
type NotesRequirement = "NONE" | "OPTIONAL" | "REQUIRED";

interface ReasonCode {
    code: string;
    label: string;
    appliesTo: RejectionType[];
    notesRequirement: NotesRequirement;
    sortOrder: number;
}

interface RejectFormProps {
    onSubmit: (rejectionType: RejectionType, feedbackText: string, reasonCodes: string[]) => Promise<void>;
    onCancel: () => void;
    submitting: boolean;
    dark?: boolean;
    supersedesApproval?: boolean;
}

function RejectForm({ onSubmit, onCancel, submitting, dark, supersedesApproval }: RejectFormProps) {
    const [rejectionType, setRejectionType] = useState<RejectionType>("ADVISORY_ONLY");
    const [feedbackText, setFeedbackText] = useState("");
    const [selectedReasons, setSelectedReasons] = useState<string[]>([]);

    const { data: reasonCodesData, isLoading: reasonCodesLoading } = useQuery<{ success: boolean; data: ReasonCode[] }>({
        queryKey: ["/api/review/reason-codes", rejectionType],
        queryFn: async () => getJson<{ success: boolean; data: ReasonCode[] }>(`/api/review/reason-codes?rejectionType=${rejectionType}`),
        staleTime: 5 * 60 * 1000,
    });

    const reasonCodes = (reasonCodesData?.data || [])
        .slice()
        .sort((a, b) => a.sortOrder - b.sortOrder || a.label.localeCompare(b.label));
    const notesRequired = selectedReasons.some((code) => {
        const reason = reasonCodes.find((item) => item.code === code);
        return reason?.notesRequirement === "REQUIRED" || code === "OTHER";
    });
    const mustPickConfiguredReason = reasonCodes.length > 0;
    const advisoryNoteOnly =
        rejectionType === "ADVISORY_ONLY" && !mustPickConfiguredReason && feedbackText.trim().length > 0;
    const canConfirmReject =
        !submitting
        && !reasonCodesLoading
        && !(notesRequired && !feedbackText.trim())
        && (mustPickConfiguredReason
            ? selectedReasons.length > 0
            : rejectionType === "ADVISORY_ONLY"
                ? advisoryNoteOnly
                : false);

    const base = dark
        ? "rounded-lg border border-red-800 bg-red-950/80 p-3 space-y-3"
        : "mt-2 rounded-lg border border-red-200 bg-red-50 p-3 space-y-3";

    useEffect(() => {
        setSelectedReasons([]);
    }, [rejectionType]);

    function toggleReason(code: string) {
        setSelectedReasons((current) => current.includes(code)
            ? current.filter((value) => value !== code)
            : [...current, code]
        );
    }

    return (
        <div className={base}>
            <p className={`text-xs font-medium ${dark ? "text-red-300" : "text-red-700"}`}>Rejection details</p>
            {supersedesApproval && (
                <div className={[
                    "rounded-md border px-3 py-2 text-xs flex items-start gap-2",
                    dark
                        ? "border-amber-700 bg-amber-950/50 text-amber-200"
                        : "border-amber-200 bg-amber-50 text-amber-900"
                ].join(" ")}>
                    <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                    <p>
                        This will supersede the previous approval. The submission will no longer count as approved evidence.
                        {rejectionType === "ADVISORY_ONLY"
                            ? " The operator will see read-once feedback only — no repost task."
                            : " A repost task will be created for the operator."}
                    </p>
                </div>
            )}
            <div className="flex flex-wrap gap-1.5">
                {REJECTION_TYPE_DISPLAY_ORDER.map((value) => (
                    <button
                        key={value}
                        type="button"
                        onClick={() => setRejectionType(value)}
                        className={[
                            "px-2.5 py-1 rounded-full text-xs font-medium border transition-colors",
                            rejectionType === value
                                ? "bg-red-600 text-white border-red-600"
                                : dark
                                    ? "bg-transparent text-red-300 border-red-700 hover:bg-red-900"
                                    : "bg-white text-red-700 border-red-300 hover:bg-red-100"
                        ].join(" ")}
                    >
                        <span className="inline-flex items-center gap-1">
                            {EVIDENCE_DRAWER_REJECTION_LABELS[value]}
                            {value === "ADVISORY_ONLY" && (
                                <NewFeatureBadge
                                    className={
                                        rejectionType === value
                                            ? "bg-white/20 text-white border-white/30 hover:bg-white/20"
                                            : undefined
                                    }
                                />
                            )}
                        </span>
                    </button>
                ))}
            </div>
            <div className="space-y-2">
                <p className={`text-xs font-medium ${dark ? "text-red-300" : "text-red-700"}`}>Why is it being rejected?</p>
                {reasonCodesLoading ? (
                    <p className={`text-xs ${dark ? "text-red-200/80" : "text-muted-foreground"}`}>Loading reasons…</p>
                ) : reasonCodes.length > 0 ? (
                    <div className="flex flex-wrap gap-1.5">
                        {reasonCodes.map((reason) => {
                            const selected = selectedReasons.includes(reason.code);
                            return (
                                <button
                                    key={reason.code}
                                    type="button"
                                    onClick={() => toggleReason(reason.code)}
                                    className={[
                                        "px-2.5 py-1 rounded-full text-xs font-medium border transition-colors",
                                        selected
                                            ? "bg-red-600 text-white border-red-600"
                                            : dark
                                                ? "bg-transparent text-red-300 border-red-700 hover:bg-red-900"
                                                : "bg-white text-red-700 border-red-300 hover:bg-red-100"
                                    ].join(" ")}
                                >
                                    {reason.label}
                                </button>
                            );
                        })}
                    </div>
                ) : rejectionType === "ADVISORY_ONLY" ? (
                    <p className={`text-xs ${dark ? "text-red-200/80" : "text-muted-foreground"}`}>
                        No reason chips for advisory yet — add a note below to continue.
                    </p>
                ) : (
                    <p className={`text-xs ${dark ? "text-red-200/80" : "text-muted-foreground"}`}>No rejection reasons configured.</p>
                )}
                {selectedReasons.length === 0 && !reasonCodesLoading && reasonCodes.length > 0 && (
                    <p className={`text-xs ${dark ? "text-red-200/80" : "text-red-700"}`}>Select at least one rejection reason.</p>
                )}
            </div>
            <Textarea
                placeholder={notesRequired ? "Notes required for selected reason…" : "Optional note for operator…"}
                value={feedbackText}
                onChange={e => setFeedbackText(e.target.value)}
                className={`h-20 text-xs resize-none ${dark ? "bg-black/40 border-red-800 text-white placeholder:text-red-400" : ""}`}
            />
            {notesRequired && !feedbackText.trim() && (
                <p className={`text-xs ${dark ? "text-red-200/80" : "text-red-700"}`}>Notes are required for the selected reason.</p>
            )}
            <div className="flex gap-2">
                <Button
                    size="sm"
                    variant="destructive"
                    className="gap-1.5"
                    disabled={!canConfirmReject}
                    onClick={() => onSubmit(rejectionType, feedbackText, selectedReasons)}
                >
                    <ThumbsDown className="h-3.5 w-3.5" />
                    {submitting ? "Rejecting…" : "Confirm reject"}
                </Button>
                <Button
                    size="sm"
                    variant={dark ? "ghost" : "outline"}
                    className={dark ? "text-white hover:bg-white/10" : ""}
                    onClick={onCancel}
                    disabled={submitting}
                >
                    Cancel
                </Button>
            </div>
        </div>
    );
}

// ─── Review controls (shared between drawer card and lightbox) ─────────────────

interface ReviewControlsProps {
    task: TrackerTask;
    onDecision: (postingEventId: number, decision: "APPROVE" | "REJECT", rejectionType?: RejectionType, feedbackText?: string, reasonCodes?: string[], retailerReady?: boolean) => Promise<void>;
    onPromoteRetailerReady?: (postingEventId: number) => Promise<void>;
    dark?: boolean;
    activeRetailerStandards?: TrackerEvidenceResponse['activeRetailerStandards'];
    sourcePostingReferences?: TrackerSourcePostingReference[];
}

function ReviewControls({ task, onDecision, onPromoteRetailerReady, dark, activeRetailerStandards, sourcePostingReferences = [] }: ReviewControlsProps) {
    const [rejectOpen, setRejectOpen] = useState(false);
    const [guidanceOpen, setGuidanceOpen] = useState(false);
    const [approveInsteadOpen, setApproveInsteadOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [promoting, setPromoting] = useState(false);
    const [reviewError, setReviewError] = useState<ReviewErrorState | null>(null);
    const canReview = isTrackerTaskReviewable(task);
    const hasStandards = Boolean(activeRetailerStandards);

    async function handleApprove(retailerReady = false) {
        setSubmitting(true);
        setReviewError(null);
        try {
            await onDecision(task.postingEventId, "APPROVE", undefined, undefined, undefined, retailerReady);
        } catch (err) {
            setReviewError({
                message: err instanceof Error ? err.message : "Approval failed",
                repostTaskId: err instanceof ReviewDecisionError ? err.repostTaskId : null,
            });
        } finally {
            setSubmitting(false);
        }
    }

    async function handlePromoteRetailerReady() {
        if (!onPromoteRetailerReady) return;
        setPromoting(true);
        try { await onPromoteRetailerReady(task.postingEventId); }
        finally { setPromoting(false); }
    }

    async function handleRejectSubmit(rejectionType: RejectionType, feedbackText: string, reasonCodes: string[]) {
        setSubmitting(true);
        setReviewError(null);
        try {
            await onDecision(task.postingEventId, "REJECT", rejectionType, feedbackText, reasonCodes);
            setRejectOpen(false);
        } catch (err) {
            setReviewError({
                message: err instanceof Error ? err.message : "Rejection failed",
                repostTaskId: err instanceof ReviewDecisionError ? err.repostTaskId : null,
            });
        } finally { setSubmitting(false); }
    }

    if (!canReview) return null;

    const scopeHint = getReferenceKindGroupCount(sourcePostingReferences) > 0 ? (
        <p className={`text-xs leading-snug ${dark ? "text-sky-300" : "text-sky-800"}`}>
            {getReviewDecisionScopeHint(task.taskType, sourcePostingReferences)}
        </p>
    ) : null;

    if (task.reviewStatus === 'approved') {
        return (
            <div className="space-y-1">
                {scopeHint}
                <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-xs flex items-center gap-1 ${dark ? "text-green-400" : "text-green-700"}`}>
                        <CheckCircle2 className="h-3.5 w-3.5" /> Approved
                    </span>
                    {task.retailerReady && (
                        <span className={`text-xs flex items-center gap-1 ${dark ? "text-blue-400" : "text-blue-700"}`}>
                            <ShieldCheck className="h-3 w-3" /> Retailer Ready
                        </span>
                    )}
                    {hasStandards && !task.retailerReady && onPromoteRetailerReady && (
                        <Button
                            size="sm"
                            variant="outline"
                            className={dark ? "h-7 px-2.5 text-xs gap-1.5 text-blue-400 border-blue-700 hover:bg-blue-900/40" : "h-7 px-2.5 text-xs gap-1.5 text-blue-700 border-blue-200 hover:bg-blue-50"}
                            disabled={promoting}
                            onClick={handlePromoteRetailerReady}
                        >
                            <ShieldCheck className="h-3 w-3" />
                            {promoting ? "Promoting..." : "Mark as Retailer Ready"}
                        </Button>
                    )}
                    <button
                        type="button"
                        className={`text-xs underline hover:no-underline ${dark ? "text-gray-400" : "text-muted-foreground"}`}
                        onClick={() => setRejectOpen(o => !o)}
                    >
                        Reject instead?
                    </button>
                </div>
                {reviewError && (
                    <div className={`text-xs px-2 py-1 rounded space-y-2 ${dark ? "bg-red-900/60 text-red-300" : "bg-red-50 text-red-700 border border-red-200"}`}>
                        <p>{reviewError.message}</p>
                        {reviewError.repostTaskId && (
                            <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                className="h-7 text-xs"
                                onClick={() => {
                                    window.location.href = `/tasks/assignments?tab=mobile&assignmentId=${reviewError.repostTaskId}`;
                                }}
                            >
                                Open assignments
                            </Button>
                        )}
                    </div>
                )}
                {rejectOpen && (
                    <RejectForm
                        onSubmit={handleRejectSubmit}
                        onCancel={() => setRejectOpen(false)}
                        submitting={submitting}
                        dark={dark}
                        supersedesApproval
                    />
                )}
            </div>
        );
    }

    if (task.reviewStatus === 'rejected') {
        return (
            <div className="space-y-1">
                {scopeHint}
                <div className="flex items-center gap-2">
                    <span className={`text-xs flex items-center gap-1 ${dark ? "text-red-400" : "text-red-700"}`}>
                        <XCircle className="h-3.5 w-3.5" /> Rejected
                    </span>
                    <button
                        type="button"
                        className={`text-xs underline hover:no-underline ${dark ? "text-gray-400" : "text-muted-foreground"}`}
                        disabled={submitting}
                        onClick={() => setApproveInsteadOpen(true)}
                    >
                        {submitting ? "Approving…" : "Approve instead?"}
                    </button>
                </div>
                {reviewError && (
                    <div className={`text-xs px-2 py-1 rounded space-y-2 ${dark ? "bg-red-900/60 text-red-300" : "bg-red-50 text-red-700 border border-red-200"}`}>
                        <p>{reviewError.message}</p>
                        {reviewError.repostTaskId && (
                            <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                className="h-7 text-xs"
                                onClick={() => {
                                    window.location.href = `/tasks/assignments?tab=mobile&assignmentId=${reviewError.repostTaskId}`;
                                }}
                            >
                                Open assignments
                            </Button>
                        )}
                    </div>
                )}
                <AlertDialog open={approveInsteadOpen} onOpenChange={setApproveInsteadOpen}>
                    <AlertDialogContent>
                        <AlertDialogHeader>
                            <AlertDialogTitle>Approve this rejected submission?</AlertDialogTitle>
                            <AlertDialogDescription>
                                This will supersede the previous rejection and recall the repost task sent to the operator. If the operator has already started or submitted the repost, this change will be blocked.
                            </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                            <AlertDialogCancel disabled={submitting}>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                                disabled={submitting}
                                onClick={() => {
                                    setApproveInsteadOpen(false);
                                    void handleApprove(false);
                                }}
                            >
                                Continue and approve
                            </AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>
            </div>
        );
    }

    return (
        <div className="space-y-2">
            {scopeHint}
            <div className="flex items-center gap-2 flex-wrap">
                <Button
                    size="sm"
                    variant={dark ? "default" : "outline"}
                    className={dark ? "h-8 px-3 text-xs gap-1.5 bg-red-700 hover:bg-red-600 text-white border-0" : "h-7 px-2.5 text-xs gap-1.5 text-red-700 border-red-300 hover:bg-red-50"}
                    disabled={submitting}
                    onClick={() => setRejectOpen(o => !o)}
                >
                    <ThumbsDown className="h-3 w-3" />
                    Reject
                    {rejectOpen ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                </Button>
                <Button
                    size="sm"
                    variant={dark ? "default" : "outline"}
                    className={dark ? "h-8 px-3 text-xs gap-1.5 bg-green-600 hover:bg-green-500 text-white border-0" : "h-7 px-2.5 text-xs gap-1.5 text-green-700 border-green-300 hover:bg-green-50"}
                    disabled={submitting}
                    onClick={() => handleApprove(false)}
                >
                    <ThumbsUp className="h-3 w-3" />
                    Approve
                </Button>
                {hasStandards && (
                    <>
                        <Button
                            size="sm"
                            variant={dark ? "default" : "outline"}
                            className={dark ? "h-8 px-3 text-xs gap-1.5 bg-blue-600 hover:bg-blue-500 text-white border-0" : "h-7 px-2.5 text-xs gap-1.5 text-blue-700 border-blue-200 hover:bg-blue-50"}
                            disabled={submitting}
                            onClick={() => handleApprove(true)}
                        >
                            <ShieldCheck className="h-3 w-3" />
                            Approve &amp; Retailer Ready
                        </Button>
                        <button
                            type="button"
                            className={`text-xs hover:underline ${dark ? "text-blue-400 hover:text-blue-300" : "text-blue-600 hover:text-blue-800"}`}
                            onClick={() => setGuidanceOpen(o => !o)}
                        >
                            Latest Guidance
                        </button>
                    </>
                )}
            </div>
            {guidanceOpen && hasStandards && (
                <div className={`rounded-md border p-3 text-xs max-h-48 overflow-y-auto ${dark ? "bg-blue-950/40 border-blue-800" : "bg-blue-50 border-blue-200"}`}>
                    <p className={`font-medium mb-1 ${dark ? "text-blue-300" : "text-blue-900"}`}>Image Quality for Retailer Review</p>
                    <p className="whitespace-pre-wrap">{activeRetailerStandards!.imageQualityForReview}</p>
                </div>
            )}
            {reviewError && (
                <div className={`text-xs px-2 py-1 rounded space-y-2 ${dark ? "bg-red-900/60 text-red-300" : "bg-red-50 text-red-700 border border-red-200"}`}>
                    <p>{reviewError.message}</p>
                    {reviewError.repostTaskId && (
                        <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs"
                            onClick={() => {
                                window.location.href = `/tasks/assignments?tab=mobile&assignmentId=${reviewError.repostTaskId}`;
                            }}
                        >
                            Open assignments
                        </Button>
                    )}
                </div>
            )}
            {rejectOpen && (
                <RejectForm onSubmit={handleRejectSubmit} onCancel={() => setRejectOpen(false)} submitting={submitting} dark={dark} />
            )}
        </div>
    );
}

// ─── Photo lightbox with navigation + review controls ─────────────────────────

interface LightboxProps {
    flatPhotos: FlatPhoto[];
    allTasks: TrackerTask[];
    initialIndex: number;
    storeName?: string;
    onClose: () => void;
    onDecision: (postingEventId: number, decision: "APPROVE" | "REJECT", rejectionType?: RejectionType, feedbackText?: string, reasonCodes?: string[], retailerReady?: boolean) => Promise<void>;
    onPromoteRetailerReady?: (postingEventId: number) => Promise<void>;
    activeRetailerStandards?: TrackerEvidenceResponse['activeRetailerStandards'];
}

function PhotoLightbox({ flatPhotos, allTasks, initialIndex, storeName, onClose, onDecision, onPromoteRetailerReady, activeRetailerStandards }: LightboxProps) {
    const [currentIndex, setCurrentIndex] = useState(initialIndex);
    const [zoom, setZoom] = useState(1);
    const [imageSize, setImageSize] = useState<{ width: number; height: number }>({ width: 1080, height: 1920 });
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const queryClient = useQueryClient();

    const [editingAdminNotes, setEditingAdminNotes] = useState(false);
    const [adminNotesText, setAdminNotesText] = useState("");
    const [savingAdminNotes, setSavingAdminNotes] = useState(false);

    // Inline format-quantity editing inside the carousel
    const [editingQuantities, setEditingQuantities] = useState(false);
    const [quantityValues, setQuantityValues] = useState<Record<number, number>>({});
    const [savingQuantities, setSavingQuantities] = useState(false);
    const [quantityError, setQuantityError] = useState<string | null>(null);
    const [quantitySavedAt, setQuantitySavedAt] = useState<number | null>(null);

    // Inline image editing inside the carousel
    const [removingImage, setRemovingImage] = useState(false);
    const [addingImage, setAddingImage] = useState(false);
    const [imageError, setImageError] = useState<string | null>(null);
    const addImageInputRef = useRef<HTMLInputElement | null>(null);

    const current = flatPhotos[currentIndex];
    const task = current ? allTasks[current.taskIndex] : null;
    const currentPhoto = task && current ? task.photos[current.photoIndex] : null;
    const total = flatPhotos.length;

    useEffect(() => {
        setEditingAdminNotes(false);
        setAdminNotesText(task?.adminNotes || "");
    }, [task?.postingEventId, task?.adminNotes]);

    // Reset quantity-edit state whenever the user navigates to a different task.
    // Photos within the same task share the same posting event, so editing should persist
    // across photos for that task.
    useEffect(() => {
        setEditingQuantities(false);
        setQuantityValues({});
        setQuantityError(null);
        setQuantitySavedAt(null);
    }, [task?.postingEventId]);

    // Auto-clear "Saved" confirmation after a few seconds
    useEffect(() => {
        if (!quantitySavedAt) return;
        const handle = window.setTimeout(() => setQuantitySavedAt(null), 3000);
        return () => window.clearTimeout(handle);
    }, [quantitySavedAt]);

    // Keep the carousel index valid as photos are added/removed (the flatPhotos
    // prop changes after a mutation refetch). Close the lightbox if none remain.
    useEffect(() => {
        if (total === 0) {
            onClose();
            return;
        }
        if (currentIndex > total - 1) {
            setCurrentIndex(total - 1);
        }
    }, [total, currentIndex, onClose]);

    async function saveAdminNotes() {
        if (!task?.reviewDecisionId) return;
        setSavingAdminNotes(true);
        try {
            const res = await apiFetch(`/api/review/decisions/${task.reviewDecisionId}/admin-notes`, {
                method: "PATCH",
                body: JSON.stringify({ adminNotes: adminNotesText }),
                headers: { "Content-Type": "application/json" },
            });
            if (!res.ok) throw new Error("Failed to save");
            setEditingAdminNotes(false);
            queryClient.invalidateQueries({ queryKey: ["tracker-evidence"] });
        } catch {
            // keep editing open on failure
        } finally {
            setSavingAdminNotes(false);
        }
    }

    // Quantity editing — mirrors TaskCard's "Correct Submission" flow but stays inside the carousel.
    const editableFormats = task?.mediaFormats ?? [];
    const canEditQuantities = !!task
        && task.postingEventId > 0
        && task.status !== 'pending'
        && editableFormats.some((fmt) => fmt.formatEvidenceId != null);

    function enterQuantityEditMode() {
        if (!task) return;
        const initial: Record<number, number> = {};
        for (const fmt of task.mediaFormats) {
            if (fmt.formatEvidenceId != null) {
                initial[fmt.formatEvidenceId] = fmt.delivered;
            }
        }
        setQuantityValues(initial);
        setQuantityError(null);
        setQuantitySavedAt(null);
        setEditingQuantities(true);
    }

    function cancelQuantityEdit() {
        setEditingQuantities(false);
        setQuantityValues({});
        setQuantityError(null);
    }

    const hasQuantityChanges = !!task && task.mediaFormats.some((fmt) => {
        const feId = fmt.formatEvidenceId;
        if (feId == null) return false;
        const newVal = quantityValues[feId];
        return newVal !== undefined && newVal !== fmt.delivered;
    });

    async function saveQuantityCorrections() {
        if (!task) return;
        setSavingQuantities(true);
        setQuantityError(null);
        try {
            const corrections: Array<{ formatEvidenceId: number; newActualCount: number }> = [];
            for (const fmt of task.mediaFormats) {
                const feId = fmt.formatEvidenceId;
                if (feId == null) continue;
                const newVal = quantityValues[feId];
                if (newVal !== undefined && newVal !== fmt.delivered) {
                    corrections.push({ formatEvidenceId: feId, newActualCount: newVal });
                }
            }
            if (corrections.length === 0) {
                setEditingQuantities(false);
                return;
            }
            const res = await apiFetch(`/api/review/submissions/${task.postingEventId}/bulk-correct`, {
                method: "POST",
                body: JSON.stringify({ corrections, reason: "Admin correction via photo carousel" }),
                headers: { "Content-Type": "application/json" },
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.error || `Save failed (${res.status})`);
            }
            setEditingQuantities(false);
            setQuantityValues({});
            setQuantitySavedAt(Date.now());
            await queryClient.invalidateQueries({ queryKey: ["tracker-evidence"] });
            await queryClient.invalidateQueries({ queryKey: ["tracker-stores"] });
        } catch (err) {
            setQuantityError(err instanceof Error ? err.message : "Failed to save corrections");
        } finally {
            setSavingQuantities(false);
        }
    }

    const canEditImages = !!task && task.postingEventId > 0;

    async function removeCurrentImage() {
        if (!task || !currentPhoto) return;
        const assetId = Number(currentPhoto.photoId.replace("asset_", ""));
        if (!Number.isFinite(assetId)) return;
        if (!window.confirm("Retract this image from the submission? It will be hidden from review and reporting but kept in the audit history.")) return;
        setRemovingImage(true);
        setImageError(null);
        try {
            const res = await apiFetch(`/api/review/submissions/${task.postingEventId}/images/${assetId}`, {
                method: "DELETE",
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.error || `Remove failed (${res.status})`);
            }
            await queryClient.invalidateQueries({ queryKey: ["tracker-evidence"] });
            await queryClient.invalidateQueries({ queryKey: ["tracker-stores"] });
        } catch (err) {
            setImageError(err instanceof Error ? err.message : "Failed to retract image");
        } finally {
            setRemovingImage(false);
        }
    }

    async function addImage(file: File) {
        if (!task) return;
        setAddingImage(true);
        setImageError(null);
        try {
            const formData = new FormData();
            formData.append("photo", file);
            const res = await apiFetch(`/api/review/submissions/${task.postingEventId}/images`, {
                method: "POST",
                body: formData,
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.error || `Upload failed (${res.status})`);
            }
            await queryClient.invalidateQueries({ queryKey: ["tracker-evidence"] });
            await queryClient.invalidateQueries({ queryKey: ["tracker-stores"] });
        } catch (err) {
            setImageError(err instanceof Error ? err.message : "Failed to add image");
        } finally {
            setAddingImage(false);
        }
    }

    const prev = useCallback(() => setCurrentIndex(i => (i > 0 ? i - 1 : total - 1)), [total]);
    const next = useCallback(() => setCurrentIndex(i => (i < total - 1 ? i + 1 : 0)), [total]);
    const zoomIn = useCallback(() => setZoom((value) => Math.min(value + 0.25, 3)), []);
    const zoomOut = useCallback(() => setZoom((value) => Math.max(value - 0.25, 1)), []);
    const resetZoom = useCallback(() => setZoom(1), []);

    useEffect(() => {
        setZoom(1);
    }, [currentIndex]);

    useEffect(() => {
        if (!current?.fullUrl) {
            return;
        }

        const image = new window.Image();
        image.onload = () => {
            if (image.naturalWidth > 0 && image.naturalHeight > 0) {
                setImageSize({ width: image.naturalWidth, height: image.naturalHeight });
            }
        };
        image.src = current.fullUrl;
    }, [current?.fullUrl]);

    // Keyboard navigation
    useEffect(() => {
        function onKey(e: KeyboardEvent) {
            if (e.key === "+" || e.key === "=") zoomIn();
            else if (e.key === "-" || e.key === "_") zoomOut();
            else if (e.key === "0") resetZoom();
            else if (e.key === "ArrowLeft") prev();
            else if (e.key === "ArrowRight") next();
            else if (e.key === "Escape") onClose();
        }
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [prev, next, onClose, zoomIn, zoomOut, resetZoom]);

    // Canvas sizing + draw must run before any early return so hook order stays
    // stable when flatPhotos shrinks (e.g. Unreviewed filter after approve/reject).
    const viewportWidth = typeof window !== "undefined" ? Math.max(window.innerWidth - 220, 320) : 960;
    const viewportHeight = typeof window !== "undefined" ? Math.max(window.innerHeight - 280, 320) : 720;
    const baseScale = Math.min(viewportWidth / imageSize.width, viewportHeight / imageSize.height, 1);
    const baseWidth = Math.round(imageSize.width * baseScale);
    const baseHeight = Math.round(imageSize.height * baseScale);

    useEffect(() => {
        if (!current?.fullUrl || !canvasRef.current) {
            return;
        }

        const canvas = canvasRef.current;
        const image = new window.Image();

        image.onload = () => {
            const drawWidth = Math.max(1, Math.round(baseWidth * zoom));
            const drawHeight = Math.max(1, Math.round(baseHeight * zoom));
            canvas.width = drawWidth;
            canvas.height = drawHeight;
            canvas.style.width = `${drawWidth}px`;
            canvas.style.height = `${drawHeight}px`;

            const ctx = canvas.getContext("2d");
            if (!ctx) {
                return;
            }

            ctx.clearRect(0, 0, drawWidth, drawHeight);
            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = "high";
            ctx.drawImage(image, 0, 0, drawWidth, drawHeight);
        };

        image.src = current.fullUrl;
    }, [current?.fullUrl, baseWidth, baseHeight, zoom]);

    if (!current || !task) return null;

    // Photo index within this task (e.g. "2 of 4")
    const photosForTask = flatPhotos.filter(p => p.taskIndex === current.taskIndex);
    const photoNumInTask = photosForTask.findIndex(p => p.globalIndex === current.globalIndex) + 1;
    const reviewNotes = task.rejectionNotes || task.reviewFeedbackText;
    const sourcePostingReferences = (task.taskType === 'REMOVAL' || task.taskType === 'REPOST')
        ? (task.sourcePostingReferences ?? [])
        : [];
    const referenceCount = getReferenceKindGroupCount(sourcePostingReferences);
    const actionTimestampLabel = getTaskTypeActionLabel(task.taskType);

    return (
        <div className="absolute inset-0 bg-white flex flex-col" style={{ zIndex: 9999 }}>
            {/* Top bar */}
            <div className="flex items-center justify-between px-4 py-3 border-b flex-shrink-0">
                <div className="flex flex-col">
                    {storeName && (
                        <span className="text-muted-foreground text-xs font-medium uppercase tracking-wide mb-0.5">
                            {storeName}
                        </span>
                    )}
                    <div className="text-foreground font-medium text-sm flex items-center gap-2 flex-wrap">
                        {task.taskType === 'REMOVAL' ? (
                            <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-orange-700 bg-orange-50 border border-orange-200 rounded-full px-2 py-0.5">
                                <Trash2 className="h-3 w-3" /> Removal
                            </span>
                        ) : task.taskType === 'REPOST' ? (
                            <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-amber-800 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
                                <RotateCcw className="h-3 w-3" /> Repost
                            </span>
                        ) : (
                            <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-blue-700 bg-blue-50 border border-blue-200 rounded-full px-2 py-0.5">
                                <Package className="h-3 w-3" /> Install
                            </span>
                        )}
                        <span>{task.campaignName}</span>
                        {task.packName && <span className="text-muted-foreground font-normal">· {task.packName}</span>}
                    </div>
                    <span className="text-muted-foreground text-xs mt-0.5">
                        Photo {photoNumInTask} of {photosForTask.length} for this task
                        {total > 1 && ` · ${currentIndex + 1} of ${total} total`}
                    </span>
                </div>
                <div className="flex items-center gap-3">
                    <div className="flex items-center gap-1 rounded-md border bg-white p-1">
                        <button
                            type="button"
                            title="Zoom out"
                            className="text-muted-foreground hover:text-foreground p-1.5 rounded-md hover:bg-muted transition-colors disabled:opacity-40"
                            onClick={zoomOut}
                            disabled={zoom <= 1}
                            aria-label="Zoom out"
                        >
                            <ZoomOut className="h-4 w-4" />
                        </button>
                        <button
                            type="button"
                            title="Reset zoom"
                            className="min-w-12 px-2 py-1 text-xs font-medium text-muted-foreground hover:text-foreground rounded-md hover:bg-muted transition-colors"
                            onClick={resetZoom}
                            aria-label="Reset zoom"
                        >
                            {Math.round(zoom * 100)}%
                        </button>
                        <button
                            type="button"
                            title="Zoom in"
                            className="text-muted-foreground hover:text-foreground p-1.5 rounded-md hover:bg-muted transition-colors disabled:opacity-40"
                            onClick={zoomIn}
                            disabled={zoom >= 3}
                            aria-label="Zoom in"
                        >
                            <ZoomIn className="h-4 w-4" />
                        </button>
                    </div>
                    {canEditImages && (
                        <div className="flex items-center gap-1">
                            <input
                                ref={addImageInputRef}
                                type="file"
                                accept="image/*"
                                className="hidden"
                                disabled={addingImage}
                                onChange={event => {
                                    const file = event.target.files?.[0];
                                    event.target.value = "";
                                    if (file) addImage(file);
                                }}
                            />
                            <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                className="h-8 px-2.5 text-xs gap-1.5"
                                disabled={addingImage}
                                onClick={() => addImageInputRef.current?.click()}
                            >
                                <Upload className="h-3.5 w-3.5" />
                                {addingImage ? "Adding…" : "Add photo"}
                            </Button>
                            <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                className="h-8 px-2.5 text-xs gap-1.5 text-red-600 border-red-300 hover:bg-red-50"
                                disabled={removingImage}
                                onClick={removeCurrentImage}
                            >
                                <Trash2 className="h-3.5 w-3.5" />
                                {removingImage ? "Retracting…" : "Retract photo"}
                            </Button>
                        </div>
                    )}
                    <ReviewBadge status={task.reviewStatus} reviewable={isTrackerTaskReviewable(task)} />
                    <button
                        type="button"
                        title="Close"
                        className="text-muted-foreground hover:text-foreground p-1.5 rounded-md hover:bg-muted transition-colors"
                        onClick={onClose}
                        aria-label="Close"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>
            </div>

            {imageError && (
                <div className="px-4 py-2 text-xs text-red-600 bg-red-50 border-b border-red-200 flex-shrink-0">
                    {imageError}
                </div>
            )}

            {/* Photo + nav */}
            <div className="flex-1 relative min-h-0 bg-gray-50">
                {total > 1 && (
                    <button
                        type="button"
                        className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700 p-2 rounded-full hover:bg-white shadow transition-colors z-10"
                        onClick={prev}
                        aria-label="Previous photo"
                    >
                        <ChevronLeft className="h-8 w-8" />
                    </button>
                )}

                <div className="h-full w-full overflow-auto">
                    <div className="flex min-h-full items-start justify-center gap-6 px-8 py-6 xl:px-16">
                        {referenceCount > 0 ? (
                            <RemovalSourcePostingReferences
                                references={sourcePostingReferences}
                                variant="lightbox"
                            />
                        ) : null}
                        <CurrentSubmissionEvidencePanel
                            taskType={task.taskType}
                            attachedCount={photosForTask.length}
                            referenceCount={referenceCount}
                            variant="lightbox"
                        >
                            <canvas
                                ref={canvasRef}
                                aria-label="Current submission evidence under review"
                                className={[
                                    "rounded-lg shadow transition-[width,height] duration-150 select-none",
                                    zoom > 1 ? "cursor-zoom-out self-start" : "cursor-zoom-in"
                                ].join(" ")}
                                style={{ width: `${Math.round(baseWidth * zoom)}px`, height: `${Math.round(baseHeight * zoom)}px` }}
                                onClick={() => {
                                    if (zoom > 1) resetZoom();
                                    else setZoom(2);
                                }}
                            />
                        </CurrentSubmissionEvidencePanel>

                        <aside className="hidden xl:block w-[340px] flex-shrink-0 rounded-xl border bg-white/95 p-4 shadow-sm">
                            <div className="space-y-4">
                                <div>
                                    <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Installer</div>
                                    <div className="mt-1 text-sm font-medium text-foreground">{task?.operatorName || "Unknown installer"}</div>
                                </div>

                                <div>
                                    <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{actionTimestampLabel}</div>
                                    <div className="mt-1 text-sm text-foreground">
                                        {task.completedAt ? formatTrackerVisitDateTime(task.completedAt) : `${actionTimestampLabel.toLowerCase()} time not recorded`}
                                    </div>
                                </div>

                                {task.mediaFormats && task.mediaFormats.length > 0 && (
                                    <div>
                                        <div className="flex items-center justify-between">
                                            <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Format Quantities</div>
                                            {!editingQuantities && canEditQuantities && (
                                                <button
                                                    type="button"
                                                    className="text-xs text-amber-700 hover:text-amber-800 flex items-center gap-0.5"
                                                    onClick={enterQuantityEditMode}
                                                >
                                                    <Edit3 className="h-3 w-3" /> Edit
                                                </button>
                                            )}
                                        </div>

                                        {editingQuantities ? (
                                            <div className="mt-1 space-y-2">
                                                <CorrectionTable
                                                    formats={task.mediaFormats}
                                                    values={quantityValues}
                                                    onChange={(feId, val) => setQuantityValues((prev) => ({ ...prev, [feId]: val }))}
                                                    deliveredLabel={task.taskType === 'REMOVAL' ? 'Removed' : 'Delivered'}
                                                />
                                                {quantityError && (
                                                    <p className="text-xs text-red-600">{quantityError}</p>
                                                )}
                                                <div className="flex gap-1.5">
                                                    <Button
                                                        size="sm"
                                                        className="h-7 px-2 text-xs gap-1"
                                                        disabled={savingQuantities || !hasQuantityChanges}
                                                        onClick={saveQuantityCorrections}
                                                    >
                                                        <Save className="h-3 w-3" />
                                                        {savingQuantities ? "Saving…" : "Save"}
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        className="h-7 px-2 text-xs gap-1"
                                                        disabled={savingQuantities}
                                                        onClick={cancelQuantityEdit}
                                                    >
                                                        <Undo2 className="h-3 w-3" />
                                                        Cancel
                                                    </Button>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="mt-1">
                                                <div className="rounded-lg border bg-muted/40 p-2">
                                                    <table className="w-full text-xs">
                                                        <thead>
                                                            <tr className="text-muted-foreground">
                                                                <th className="text-left font-medium pb-1">Format</th>
                                                                <th className="text-right font-medium pb-1 w-14">Planned</th>
                                                                <th className="text-right font-medium pb-1 w-20">{task.taskType === 'REMOVAL' ? 'Removed' : 'Delivered'}</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {task.mediaFormats.map((fmt) => {
                                                                const isComplete = fmt.delivered >= fmt.planned;
                                                                return (
                                                                    <tr key={fmt.formatId} className="border-t">
                                                                        <td className="py-1 font-medium">{fmt.formatName}</td>
                                                                        <td className="py-1 text-right">
                                                                            <Badge variant="outline" className="bg-blue-50 text-blue-700 text-xs px-1.5">
                                                                                {fmt.planned}
                                                                            </Badge>
                                                                        </td>
                                                                        <td className="py-1 text-right">
                                                                            <Badge
                                                                                variant="outline"
                                                                                className={`text-xs px-1.5 ${isComplete ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'}`}
                                                                            >
                                                                                {fmt.delivered}
                                                                            </Badge>
                                                                        </td>
                                                                    </tr>
                                                                );
                                                            })}
                                                        </tbody>
                                                    </table>
                                                </div>
                                                {quantitySavedAt && (
                                                    <p className="mt-1 text-xs text-green-700 flex items-center gap-1">
                                                        <CheckCircle2 className="h-3 w-3" /> Quantities saved
                                                    </p>
                                                )}
                                                {!canEditQuantities && (
                                                    <p className="mt-1 text-[11px] text-muted-foreground italic">
                                                        Editing unavailable until the operator submits format-level evidence.
                                                    </p>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}

                                <div>
                                    <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Installer Note</div>
                                    <div className="mt-1 rounded-lg bg-muted/60 px-3 py-2 text-sm text-foreground min-h-16">
                                        {task.installerNote?.trim() || currentPhoto?.installerNote?.trim() || "No installer notes"}
                                    </div>
                                </div>

                                {task.notes?.trim() && (
                                    <div>
                                        <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Discrepancy Note</div>
                                        <div className="mt-1 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-900 min-h-16">
                                            {task.notes}
                                        </div>
                                    </div>
                                )}

                                <div>
                                    <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Rejection Notes</div>
                                    <div className="mt-1 rounded-lg bg-muted/60 px-3 py-2 text-sm text-foreground min-h-20">
                                        {reviewNotes?.trim() || "No rejection notes"}
                                    </div>
                                </div>

                                <div>
                                    <div className="flex items-center justify-between">
                                        <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Admin Notes</div>
                                        {task.reviewDecisionId && !editingAdminNotes && (
                                            <button
                                                type="button"
                                                className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-0.5"
                                                onClick={() => { setAdminNotesText(task.adminNotes || ""); setEditingAdminNotes(true); }}
                                            >
                                                <Edit3 className="h-3 w-3" /> Edit
                                            </button>
                                        )}
                                    </div>
                                    {editingAdminNotes ? (
                                        <div className="mt-1 space-y-1.5">
                                            <Textarea
                                                value={adminNotesText}
                                                onChange={e => setAdminNotesText(e.target.value)}
                                                placeholder="Internal note — e.g. why this was approved…"
                                                className="h-24 text-xs resize-none"
                                                autoFocus
                                            />
                                            <div className="flex gap-1.5">
                                                <Button size="sm" variant="default" className="h-7 text-xs" disabled={savingAdminNotes} onClick={saveAdminNotes}>
                                                    <Save className="h-3 w-3 mr-1" /> Save
                                                </Button>
                                                <Button size="sm" variant="ghost" className="h-7 text-xs" disabled={savingAdminNotes} onClick={() => setEditingAdminNotes(false)}>
                                                    Cancel
                                                </Button>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="mt-1 rounded-lg bg-muted/60 px-3 py-2 text-sm text-foreground min-h-16">
                                            {task.adminNotes?.trim() || "No admin notes"}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </aside>
                    </div>
                </div>

                {total > 1 && (
                    <button
                        type="button"
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700 p-2 rounded-full hover:bg-white shadow transition-colors z-10"
                        onClick={next}
                        aria-label="Next photo"
                    >
                        <ChevronRight className="h-8 w-8" />
                    </button>
                )}
            </div>

            {/* Bottom bar — quantities + review controls */}
            <div className="px-4 py-3 border-t flex-shrink-0 space-y-2">
                {/* Format quantities */}
                {task.mediaFormats && task.mediaFormats.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                        {task.mediaFormats.map(fmt => {
                            const ok = fmt.delivered >= fmt.planned;
                            return (
                                <span
                                    key={fmt.formatId}
                                    className={[
                                        "inline-flex items-center gap-1 text-xs rounded-full px-2.5 py-0.5 font-medium border",
                                        ok
                                            ? "bg-green-50 text-green-700 border-green-200"
                                            : "bg-amber-50 text-amber-700 border-amber-200"
                                    ].join(" ")}
                                >
                                    {fmt.formatName}
                                    <span className="opacity-50">·</span>
                                    <span>{fmt.delivered}/{fmt.planned}</span>
                                </span>
                            );
                        })}
                    </div>
                )}

                {/* Original task requirements stay visible while reviewing photos. */}
                <section aria-label="Task requirements" className="min-w-0 text-xs lg:mx-auto lg:max-w-xl">
                    <p className="mb-1 truncate text-muted-foreground" title={task.campaignName}>{task.campaignName}</p>
                    <dl className="space-y-1">
                        <div className="flex gap-2">
                            <dt className="shrink-0 font-medium">In-store location:</dt>
                            <dd className="min-w-0 break-words">{task.instoreLocationName?.trim() || "Not specified"}</dd>
                        </div>
                        <div className="flex items-start gap-2">
                            <dt className="shrink-0 font-medium">Posting instruction:</dt>
                            <dd className="min-w-0 flex-1">
                                {task.postingInstructionsText?.trim() ? (
                                    <details key={task.taskId}>
                                        <summary className="cursor-pointer list-none">
                                            <span className="line-clamp-1" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(task.postingInstructionsText) }} />
                                            <span className="text-primary">Expand / collapse</span>
                                        </summary>
                                        <div className="prose prose-sm mt-1 max-h-40 max-w-none overflow-y-auto break-words whitespace-pre-wrap"
                                            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(task.postingInstructionsText) }} />
                                    </details>
                                ) : "Not specified"}
                            </dd>
                        </div>
                    </dl>
                </section>

                {/* Task status + review controls */}
                <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-2 flex-shrink-0">
                        {getTaskStatusBadge(task.status)}
                        {task.reviewDecidedBy && task.reviewDecidedAt && (
                            <span className="text-xs text-muted-foreground">
                                {task.reviewStatus === 'approved' ? 'Approved' : 'Rejected'} by {task.reviewDecidedBy}{' '}
                                {formatDistanceToNow(new Date(task.reviewDecidedAt), { addSuffix: true })}
                            </span>
                        )}
                    </div>
                    <ReviewControls
                        task={task}
                        onDecision={onDecision}
                        onPromoteRetailerReady={onPromoteRetailerReady}
                        activeRetailerStandards={activeRetailerStandards}
                        sourcePostingReferences={sourcePostingReferences}
                    />
                </div>
            </div>

            {/* Thumbnail strip */}
            {total > 1 && (
                <div className="flex gap-1.5 px-4 pb-3 overflow-x-auto flex-shrink-0">
                    {flatPhotos.map((p, i) => (
                        <button
                            key={p.photoId}
                            type="button"
                            title={`Photo ${i + 1}`}
                            onClick={() => setCurrentIndex(i)}
                            className={[
                                "w-12 h-12 rounded flex-shrink-0 overflow-hidden border-2 transition-all",
                                i === currentIndex ? "border-primary scale-110" : "border-transparent opacity-50 hover:opacity-80"
                            ].join(" ")}
                        >
                            <img src={p.thumbUrl} alt="" className="w-full h-full object-cover" />
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

// ─── Manager signature block ───────────────────────────────────────────────────

function ManagerSignatureBlock({ sig }: { sig: TrackerManagerSignature }) {
    const [lightbox, setLightbox] = useState<string | null>(null);
    const isTyped = sig.signoffMethod === 'typed_name';

    return (
        <>
            <div className="rounded-lg border border-green-200 bg-green-50 p-4 space-y-3">
                <div className="flex items-center gap-2 text-green-700 font-medium text-sm">
                    <CheckCircle2 className="h-4 w-4 flex-shrink-0" />
                    Store Sign-off
                </div>
                {sig.signedBy && (
                    <div>
                        <p className="text-xs text-muted-foreground mb-0.5">Manager name</p>
                        <p className="text-sm font-medium">{sig.signedBy}</p>
                    </div>
                )}
                {sig.signedAt && (
                    <div>
                        <p className="text-xs text-muted-foreground mb-0.5">Signed at</p>
                        <p className="text-sm">{format(new Date(sig.signedAt), "dd MMM yyyy, HH:mm")}</p>
                    </div>
                )}
                {sig.signatureUrl && !isTyped && (
                    <div>
                        <p className="text-xs text-muted-foreground mb-1.5 flex items-center gap-1">
                            <PenLine className="h-3 w-3" /> Signature
                        </p>
                        <button
                            type="button"
                            onClick={() => setLightbox(sig.signatureUrl!)}
                            className="block rounded border bg-white overflow-hidden hover:ring-2 ring-primary transition-all max-w-[220px]"
                        >
                            <img src={sig.signatureUrl} alt="Manager signature" className="w-full h-auto object-contain max-h-24" />
                        </button>
                    </div>
                )}
                {isTyped && !sig.signatureUrl && (
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground italic">
                        <PenLine className="h-3 w-3" /> Name typed (no handwritten signature)
                    </div>
                )}
            </div>
            {lightbox && (
                <div
                    className="fixed inset-0 bg-black/90 flex items-center justify-center p-4"
                    style={{ zIndex: 9999 }}
                    onClick={() => setLightbox(null)}
                >
                    <button type="button" title="Close" className="absolute top-4 right-4 text-white hover:text-gray-300 p-2" onClick={() => setLightbox(null)}>
                        <X className="h-8 w-8" />
                    </button>
                    <img src={lightbox} alt="Manager signature full size" className="max-w-full max-h-full object-contain rounded-lg bg-white p-4" onClick={e => e.stopPropagation()} />
                </div>
            )}
        </>
    );
}

// ─── Review progress bar ───────────────────────────────────────────────────────

function ReviewProgressBar({
    summary, onApproveAll, onOpenRejectAll, onRejectAll, approvingAll, rejectAllOpen, rejectingAll, onRejectAllCancel
}: {
    summary: TrackerEvidenceResponse['reviewSummary'];
    onApproveAll: () => void;
    onOpenRejectAll: () => void;
    onRejectAll: (rejectionType: RejectionType, feedbackText: string, reasonCodes: string[]) => Promise<void>;
    onRejectAllCancel: () => void;
    approvingAll: boolean;
    rejectAllOpen: boolean;
    rejectingAll: boolean;
}) {
    const { total, approved, rejected, pending } = summary;
    if (total === 0) return null;
    const allApproved = approved === total && rejected === 0;

    return (
        <div className="rounded-lg border bg-muted/40 p-3 space-y-2">
            <div className="flex items-center justify-between gap-3">
                <div className="text-xs font-medium">
                    Review progress:{" "}
                    <span className={pending === 0 ? "text-green-700" : "text-muted-foreground"}>
                        {approved + rejected} of {total} reviewed
                    </span>
                    {approved > 0 && <span className="ml-2 text-green-600">· {approved} approved</span>}
                    {rejected > 0 && <span className="ml-2 text-red-600">· {rejected} rejected</span>}
                </div>
                {!allApproved ? (
                    <div className="flex items-center gap-2 flex-shrink-0">
                        <Button size="sm" variant="outline" className="h-7 px-2.5 text-xs gap-1.5 text-red-700 border-red-300 hover:bg-red-50" disabled={approvingAll || rejectingAll || pending === 0} onClick={onOpenRejectAll}>
                            <ThumbsDown className="h-3.5 w-3.5" />
                            {rejectingAll ? "Rejecting…" : "Reject all"}
                        </Button>
                        <Button size="sm" variant="outline" className="h-7 px-2.5 text-xs gap-1.5 text-green-700 border-green-300 hover:bg-green-50" disabled={approvingAll || rejectingAll || pending === 0} onClick={onApproveAll}>
                            <CheckCheck className="h-3.5 w-3.5" />
                            {approvingAll ? "Approving…" : "Approve all"}
                        </Button>
                    </div>
                ) : (
                    <span className="flex items-center gap-1 text-xs text-green-700 font-medium">
                        <CheckCheck className="h-3.5 w-3.5" /> All approved
                    </span>
                )}
            </div>
            <div className="flex h-1.5 rounded-full overflow-hidden bg-muted">
                {approved > 0 && <div className="bg-green-500 transition-all" style={{ width: `${(approved / total) * 100}%` }} />}
                {rejected > 0 && <div className="bg-red-400 transition-all" style={{ width: `${(rejected / total) * 100}%` }} />}
            </div>
            {rejectAllOpen && (
                <RejectForm
                    onSubmit={onRejectAll}
                    onCancel={onRejectAllCancel}
                    submitting={rejectingAll}
                />
            )}
        </div>
    );
}

function ReviewFilterBar({
    value,
    counts,
    onChange
}: {
    value: TrackerReviewFilter;
    counts: Record<TrackerReviewFilter, number>;
    onChange: (value: TrackerReviewFilter) => void;
}) {
    const filters: Array<{ value: TrackerReviewFilter; label: string }> = [
        { value: "all", label: "All" },
        { value: "unreviewed", label: "Unreviewed" },
        { value: "approved", label: "Approved" },
        { value: "rejected", label: "Rejected" },
    ];

    if (counts.all === 0) return null;

    return (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border bg-muted/30 p-2">
            <span className="text-xs font-medium text-muted-foreground px-1">Show</span>
            <div className="flex flex-wrap items-center gap-1">
                {filters.map((filter) => {
                    const active = value === filter.value;
                    return (
                        <button
                            key={filter.value}
                            type="button"
                            onClick={() => onChange(filter.value)}
                            className={[
                                "rounded-md px-2.5 py-1 text-xs font-medium transition-colors",
                                active
                                    ? "bg-white text-foreground shadow-sm ring-1 ring-border"
                                    : "text-muted-foreground hover:bg-white/70 hover:text-foreground"
                            ].join(" ")}
                        >
                            {filter.label}
                            <span className="ml-1 tabular-nums text-muted-foreground">{counts[filter.value]}</span>
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

// ─── Task card ─────────────────────────────────────────────────────────────────

// ─── Correction mode table (replaces FormatStatusTable when editing) ────────────

interface CorrectionTableProps {
    formats: TrackerMediaFormat[];
    values: Record<number, number>;
    onChange: (formatEvidenceId: number, val: number) => void;
    deliveredLabel: string;
}

function CorrectionTable({ formats, values, onChange, deliveredLabel }: CorrectionTableProps) {
    return (
        <div className="bg-amber-50 rounded p-2 ring-1 ring-amber-300">
            <table className="w-full text-xs">
                <thead>
                    <tr className="text-muted-foreground">
                        <th className="text-left font-medium pb-1">Format</th>
                        <th className="text-right font-medium pb-1 w-16">Planned</th>
                        <th className="text-right font-medium pb-1 w-24">{deliveredLabel}</th>
                    </tr>
                </thead>
                <tbody>
                    {formats.map(fmt => {
                        const feId = fmt.formatEvidenceId;
                        const editable = feId != null;
                        const currentVal = editable ? (values[feId] ?? fmt.delivered) : fmt.delivered;
                        return (
                            <tr key={fmt.formatId} className="border-t border-amber-200">
                                <td className="py-1 font-medium">{fmt.formatName}</td>
                                <td className="py-1 text-right">
                                    <Badge variant="outline" className="bg-blue-50 text-blue-700 text-xs px-1.5">
                                        {fmt.planned}
                                    </Badge>
                                </td>
                                <td className="py-1 text-right">
                                    {editable ? (
                                        <Input
                                            type="number"
                                            min={0}
                                            className="h-7 w-20 text-xs text-right ml-auto"
                                            value={currentVal}
                                            onChange={e => onChange(feId, Math.max(0, parseInt(e.target.value) || 0))}
                                        />
                                    ) : (
                                        <Badge variant="outline" className="bg-gray-50 text-gray-500 text-xs px-1.5">
                                            {fmt.delivered}
                                        </Badge>
                                    )}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

// ─── Task card ─────────────────────────────────────────────────────────────────

interface TaskCardProps {
    task: TrackerTask;
    flatPhotos: FlatPhoto[];
    onPhotoClick: (globalIndex: number) => void;
    onDecision: (postingEventId: number, decision: "APPROVE" | "REJECT", rejectionType?: RejectionType, feedbackText?: string, reasonCodes?: string[], retailerReady?: boolean) => Promise<void>;
    onPromoteRetailerReady?: (postingEventId: number) => Promise<void>;
    onCorrectionSaved: () => void;
    onAdminNotesSaved?: () => void;
    activeRetailerStandards?: TrackerEvidenceResponse['activeRetailerStandards'];
}

function TaskCard({ task, flatPhotos, onPhotoClick, onDecision, onPromoteRetailerReady, onCorrectionSaved, onAdminNotesSaved, activeRetailerStandards }: TaskCardProps) {
    const actionWord = task.taskType === 'REMOVAL' ? 'Removed' : 'Delivered';
    const hasFormatBreakdown = task.mediaFormats && task.mediaFormats.length > 0;
    const taskPhotos = flatPhotos.filter(p => {
        return task.photos.some(tp => tp.photoId === p.photoId);
    });

    const [correcting, setCorrecting] = useState(false);
    const [correctionValues, setCorrectionValues] = useState<Record<number, number>>({});
    const [saving, setSaving] = useState(false);
    const [correctionError, setCorrectionError] = useState<string | null>(null);

    const [editingAdminNotes, setEditingAdminNotes] = useState(false);
    const [adminNotesText, setAdminNotesText] = useState(task.adminNotes || "");
    const [savingAdminNotes, setSavingAdminNotes] = useState(false);
    const prevDecisionId = useRef(task.reviewDecisionId);

    useEffect(() => {
        if (!prevDecisionId.current && task.reviewDecisionId && !task.adminNotes) {
            setEditingAdminNotes(true);
            setAdminNotesText("");
        } else {
            setEditingAdminNotes(false);
            setAdminNotesText(task.adminNotes || "");
        }
        prevDecisionId.current = task.reviewDecisionId;
    }, [task.postingEventId, task.reviewDecisionId, task.adminNotes]);

    async function saveAdminNotes() {
        if (!task.reviewDecisionId) return;
        setSavingAdminNotes(true);
        try {
            const res = await apiFetch(`/api/review/decisions/${task.reviewDecisionId}/admin-notes`, {
                method: "PATCH",
                body: JSON.stringify({ adminNotes: adminNotesText }),
                headers: { "Content-Type": "application/json" },
            });
            if (!res.ok) throw new Error("Failed to save");
            setEditingAdminNotes(false);
            onAdminNotesSaved?.();
        } catch {
            // keep editing open on failure
        } finally {
            setSavingAdminNotes(false);
        }
    }

    const canCorrect = task.postingEventId > 0
        && task.status !== 'pending'
        && task.mediaFormats.some(f => f.formatEvidenceId != null);

    function enterCorrectionMode() {
        const initial: Record<number, number> = {};
        for (const fmt of task.mediaFormats) {
            if (fmt.formatEvidenceId != null) {
                initial[fmt.formatEvidenceId] = fmt.delivered;
            }
        }
        setCorrectionValues(initial);
        setCorrectionError(null);
        setCorrecting(true);
    }

    function cancelCorrection() {
        setCorrecting(false);
        setCorrectionValues({});
        setCorrectionError(null);
    }

    async function saveCorrections() {
        setSaving(true);
        setCorrectionError(null);
        try {
            const corrections: Array<{ formatEvidenceId: number; newActualCount: number }> = [];
            for (const fmt of task.mediaFormats) {
                const feId = fmt.formatEvidenceId;
                if (feId == null) continue;
                const newVal = correctionValues[feId];
                if (newVal !== undefined && newVal !== fmt.delivered) {
                    corrections.push({ formatEvidenceId: feId, newActualCount: newVal });
                }
            }
            if (corrections.length === 0) {
                setCorrecting(false);
                return;
            }
            const res = await apiFetch(`/api/review/submissions/${task.postingEventId}/bulk-correct`, {
                method: "POST",
                body: JSON.stringify({ corrections, reason: "Admin correction via evidence drawer" }),
                headers: { "Content-Type": "application/json" }
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.error || `Save failed (${res.status})`);
            }
            setCorrecting(false);
            setCorrectionValues({});
            onCorrectionSaved();
        } catch (err) {
            setCorrectionError(err instanceof Error ? err.message : "Failed to save corrections");
        } finally {
            setSaving(false);
        }
    }

    const hasChanges = task.mediaFormats.some(fmt => {
        const feId = fmt.formatEvidenceId;
        if (feId == null) return false;
        const newVal = correctionValues[feId];
        return newVal !== undefined && newVal !== fmt.delivered;
    });

    const canEditImages = task.postingEventId > 0;
    const [removingPhotoId, setRemovingPhotoId] = useState<string | null>(null);
    const [addingPhoto, setAddingPhoto] = useState(false);
    const [imageError, setImageError] = useState<string | null>(null);
    const addPhotoInputRef = useRef<HTMLInputElement>(null);

    async function handleRemovePhoto(photoId: string) {
        const assetId = Number(photoId.replace("asset_", ""));
        if (!Number.isFinite(assetId)) return;
        if (!window.confirm("Retract this image from the submission? It will be hidden from review and reporting but kept in the audit history.")) return;
        setRemovingPhotoId(photoId);
        setImageError(null);
        try {
            const res = await apiFetch(`/api/review/submissions/${task.postingEventId}/images/${assetId}`, {
                method: "DELETE",
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.error || `Remove failed (${res.status})`);
            }
            onCorrectionSaved();
        } catch (err) {
            setImageError(err instanceof Error ? err.message : "Failed to retract image");
        } finally {
            setRemovingPhotoId(null);
        }
    }

    async function handleAddPhoto(file: File) {
        setAddingPhoto(true);
        setImageError(null);
        try {
            const formData = new FormData();
            formData.append("photo", file);
            const res = await apiFetch(`/api/review/submissions/${task.postingEventId}/images`, {
                method: "POST",
                body: formData,
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.error || `Upload failed (${res.status})`);
            }
            onCorrectionSaved();
        } catch (err) {
            setImageError(err instanceof Error ? err.message : "Failed to add image");
        } finally {
            setAddingPhoto(false);
        }
    }

    return (
        <div className="border rounded-lg p-3 space-y-2">
            <div className="flex items-start justify-between gap-2">
                <div className="font-medium text-sm flex items-center gap-2 flex-wrap">
                    {task.taskType === 'REMOVAL'
                        ? <Trash2 className="h-3.5 w-3.5 text-orange-500 flex-shrink-0" />
                        : <Package className="h-3.5 w-3.5 text-blue-500 flex-shrink-0" />}
                    <span>{task.campaignName}</span>
                    {task.assignmentId && <span className="text-xs text-muted-foreground font-normal">#{task.assignmentId}</span>}
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                    {getTaskStatusBadge(task.status)}
                    <ReviewBadge status={task.reviewStatus} reviewable={isTrackerTaskReviewable(task)} />
                </div>
            </div>

            {task.reviewDecidedBy && task.reviewDecidedAt && (
                <p className="text-xs text-muted-foreground">
                    {task.reviewStatus === 'approved' ? 'Approved' : 'Rejected'} by {task.reviewDecidedBy}{' '}
                    {formatDistanceToNow(new Date(task.reviewDecidedAt), { addSuffix: true })}
                </p>
            )}

            {task.packName && <div className="text-xs text-muted-foreground">Pack: {task.packName}</div>}

            {correcting ? (
                <CorrectionTable
                    formats={task.mediaFormats}
                    values={correctionValues}
                    onChange={(feId, val) => setCorrectionValues(prev => ({ ...prev, [feId]: val }))}
                    deliveredLabel={actionWord}
                />
            ) : hasFormatBreakdown ? (
                <FormatStatusTable formats={task.mediaFormats} deliveredLabel={actionWord} compact />
            ) : (
                <FormatStatusTable
                    formats={[{ formatId: task.taskId, formatName: task.packName || task.campaignName, planned: task.expectedQuantity, delivered: task.completedQuantity }]}
                    deliveredLabel={actionWord}
                    showTotal={false}
                    compact
                />
            )}

            {correcting && (
                <div className="flex items-center gap-2 pt-1">
                    <Button
                        size="sm"
                        className="h-7 px-3 text-xs gap-1.5"
                        disabled={saving}
                        onClick={saveCorrections}
                    >
                        {hasChanges ? <Save className="h-3 w-3" /> : <Check className="h-3 w-3" />}
                        {saving ? "Saving…" : hasChanges ? "Save corrections" : "Done"}
                    </Button>
                    <Button
                        size="sm"
                        variant="outline"
                        className="h-7 px-3 text-xs gap-1.5"
                        disabled={saving}
                        onClick={cancelCorrection}
                    >
                        <Undo2 className="h-3 w-3" />
                        Cancel
                    </Button>
                    {correctionError && (
                        <span className="text-xs text-red-600">{correctionError}</span>
                    )}
                </div>
            )}

            {!correcting && canCorrect && (
                <Button
                    size="sm"
                    variant="outline"
                    className="h-7 px-2.5 text-xs gap-1.5 text-amber-700 border-amber-300 hover:bg-amber-50"
                    onClick={enterCorrectionMode}
                >
                    <Edit3 className="h-3 w-3" />
                    Correct Submission
                </Button>
            )}

            {task.photoCount > 0 && (
                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Camera className="h-3 w-3" />
                    {task.photoCount} photo{task.photoCount !== 1 ? "s" : ""}
                    <span className="text-xs text-muted-foreground/60 ml-1">— click to view full screen</span>
                </div>
            )}

            <TaskSubmissionNotes installerNote={task.installerNote} notes={task.notes} />

            {task.reviewDecisionId && (
                <div className="border-t pt-2 mt-2">
                    <div className="flex items-center justify-between">
                        <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Admin Notes</div>
                        {!editingAdminNotes && (
                            <button
                                type="button"
                                className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-0.5"
                                onClick={() => { setAdminNotesText(task.adminNotes || ""); setEditingAdminNotes(true); }}
                            >
                                <Edit3 className="h-3 w-3" /> Edit
                            </button>
                        )}
                    </div>
                    {editingAdminNotes ? (
                        <div className="mt-1 space-y-1.5">
                            <Textarea
                                value={adminNotesText}
                                onChange={e => setAdminNotesText(e.target.value)}
                                placeholder="Internal note — e.g. why this was approved…"
                                className="h-20 text-xs resize-none"
                            />
                            <div className="flex gap-1.5">
                                <Button size="sm" className="h-6 px-2 text-xs" disabled={savingAdminNotes} onClick={saveAdminNotes}>
                                    {savingAdminNotes ? "Saving…" : "Save"}
                                </Button>
                                <Button size="sm" variant="outline" className="h-6 px-2 text-xs" disabled={savingAdminNotes} onClick={() => setEditingAdminNotes(false)}>
                                    Cancel
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <div className="mt-1 rounded bg-muted/60 px-2 py-1.5 text-xs text-foreground">
                            {task.adminNotes?.trim() || "No admin notes yet"}
                        </div>
                    )}
                </div>
            )}

            {(task.photos.length > 0 || canEditImages) && (
                <div className="flex flex-wrap gap-2 mt-2">
                    {task.photos.map(photo => {
                        const fp = taskPhotos.find(p => p.photoId === photo.photoId);
                        const isRemoving = removingPhotoId === photo.photoId;
                        return (
                            <div key={photo.photoId} className="relative w-20 h-20">
                                <button
                                    type="button"
                                    onClick={() => fp && onPhotoClick(fp.globalIndex)}
                                    className="w-full h-full rounded border overflow-hidden hover:ring-2 ring-primary transition-all"
                                >
                                    <img
                                        src={photo.thumbUrl}
                                        alt="Evidence"
                                        className="w-full h-full object-cover"
                                        onError={e => { (e.target as HTMLImageElement).src = photo.fullUrl; }}
                                    />
                                </button>
                                {canEditImages && (
                                    <button
                                        type="button"
                                        title="Retract this image from the submission"
                                        aria-label="Retract this image from the submission"
                                        disabled={isRemoving}
                                        onClick={() => handleRemovePhoto(photo.photoId)}
                                        className="absolute -right-1.5 -top-1.5 rounded-full bg-white border border-red-300 text-red-600 p-1 shadow-sm hover:bg-red-50 disabled:opacity-50"
                                    >
                                        <Trash2 className="h-3 w-3" />
                                    </button>
                                )}
                            </div>
                        );
                    })}
                    {canEditImages && (
                        <>
                            <input
                                ref={addPhotoInputRef}
                                type="file"
                                accept="image/*"
                                className="hidden"
                                disabled={addingPhoto}
                                onChange={event => {
                                    const file = event.target.files?.[0];
                                    event.target.value = "";
                                    if (file) handleAddPhoto(file);
                                }}
                            />
                            <button
                                type="button"
                                title="Add a replacement or additional image"
                                aria-label="Add a replacement or additional image"
                                disabled={addingPhoto}
                                onClick={() => addPhotoInputRef.current?.click()}
                                className="w-20 h-20 rounded border border-dashed flex flex-col items-center justify-center gap-1 text-muted-foreground hover:bg-muted/50 disabled:opacity-50"
                            >
                                <Upload className="h-4 w-4" />
                                <span className="text-[10px]">{addingPhoto ? "Adding…" : "Add photo"}</span>
                            </button>
                        </>
                    )}
                </div>
            )}
            {imageError && (
                <div className="text-xs text-red-600">{imageError}</div>
            )}

            <div className="pt-1">
                <ReviewControls task={task} onDecision={onDecision} onPromoteRetailerReady={onPromoteRetailerReady} activeRetailerStandards={activeRetailerStandards} />
            </div>
        </div>
    );
}

// ─── Main drawer ───────────────────────────────────────────────────────────────

export function EvidenceDrawer({ cycleId, routeItemId, open, onOpenChange }: EvidenceDrawerProps) {
    const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
    const [approvingAll, setApprovingAll] = useState(false);
    const [rejectingAll, setRejectingAll] = useState(false);
    const [rejectAllOpen, setRejectAllOpen] = useState(false);
    const [bulkReviewError, setBulkReviewError] = useState<string | null>(null);
    const reviewScope = JSON.stringify([cycleId, routeItemId]);
    const currentReviewScope = useRef(reviewScope);
    currentReviewScope.current = reviewScope;
    const [reviewFilter, setReviewFilter] = useState<TrackerReviewFilter>("all");
    const queryClient = useQueryClient();

    const reviewPresence = useReviewPresenceLifecycle({
        enabled: isReviewPresenceEnabled(),
        cycleId: cycleId > 0 ? cycleId : null,
        surfaceActive: open,
    });

    const taskDefinitionIds = new URLSearchParams(window.location.search).get("taskDefinitionIds");
    const queryKey = ["tracker-evidence", cycleId, routeItemId, taskDefinitionIds];

    const { data, isLoading } = useQuery<TrackerEvidenceResponse>({
        queryKey,
        queryFn: () => getJson<TrackerEvidenceResponse>(
            `/api/analytics/tracker/evidence/${cycleId}/${routeItemId}${trackerApiQueryString(taskDefinitionIds)}`,
            { cache: "no-store" },
        ),
        enabled: open && !!cycleId && !!routeItemId,
        staleTime: 0,
        refetchOnMount: "always",
        refetchOnWindowFocus: "always",
        refetchInterval: open ? 15000 : false,
    });

    const effectiveData = data;
    const allTasks = effectiveData ? [...effectiveData.postings, ...effectiveData.removals] : [];
    const reviewFilterCounts = useMemo<Record<TrackerReviewFilter, number>>(
        () => buildTrackerReviewFilterCounts(allTasks),
        [allTasks],
    );
    const filteredPostings = useMemo(() => {
        if (!effectiveData) return [];
        return filterTrackerTasksByReview(effectiveData.postings, reviewFilter);
    }, [effectiveData, reviewFilter]);
    const filteredRemovals = useMemo(() => {
        if (!effectiveData) return [];
        return filterTrackerTasksByReview(effectiveData.removals, reviewFilter);
    }, [effectiveData, reviewFilter]);
    const visibleTasks = useMemo(() => [...filteredPostings, ...filteredRemovals], [filteredPostings, filteredRemovals]);
    const flatPhotos = useMemo(() => buildFlatPhotos(visibleTasks), [visibleTasks]);

    useEffect(() => {
        setReviewFilter("all");
        setLightboxIndex(null);
        setBulkReviewError(null);
    }, [cycleId, routeItemId]);

    useEffect(() => {
        if (data && reviewFilterCounts.unreviewed === 0) setBulkReviewError(null);
    }, [data, reviewFilterCounts.unreviewed]);

    useEffect(() => {
        if (!open || !isReviewPresenceEnabled() || !effectiveData?.storeId) {
            return;
        }
        if (lightboxIndex != null) {
            return;
        }
        void reviewPresence.focusEvidenceDrawerStore(effectiveData.storeId);
    }, [open, effectiveData?.storeId, lightboxIndex, reviewPresence]);

    useEffect(() => {
        if (!open || !isReviewPresenceEnabled() || lightboxIndex == null) {
            return;
        }
        const photo = flatPhotos[lightboxIndex];
        const task = photo ? visibleTasks[photo.taskIndex] : undefined;
        if (task?.postingEventId && task.postingEventId > 0) {
            void reviewPresence.focusEvidenceDrawerTask(task.postingEventId);
        }
    }, [open, lightboxIndex, flatPhotos, visibleTasks, reviewPresence]);

    async function submitDecision(
        postingEventId: number,
        decision: "APPROVE" | "REJECT",
        rejectionType?: RejectionType,
        feedbackText?: string,
        reasonCodes?: string[],
        retailerReady?: boolean
    ) {
        const res = await apiFetch("/api/review/decisions", {
            method: "POST",
            body: JSON.stringify({
                postingEventId,
                decision,
                ...(retailerReady && { retailerReady: true }),
                ...(rejectionType && { rejectionType }),
                ...(feedbackText?.trim() && { feedbackText: feedbackText.trim() }),
                ...(decision === "REJECT" && Array.isArray(reasonCodes) && reasonCodes.length > 0 && { reasonCodes }),
                ...(decision === "REJECT" && feedbackText?.trim() && { rejectionNotes: feedbackText.trim() }),
            }),
            headers: { "Content-Type": "application/json" }
        });
        if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            const formatted = formatReviewDecisionError(err, `Review submission failed (${res.status})`);
            throw new ReviewDecisionError(formatted.message, { repostTaskId: formatted.repostTaskId });
        }
        if (isReviewPresenceEnabled()) {
            reviewPresence.notifyDecisionSuccess();
            const storeId = effectiveData?.storeId;
            if (storeId) {
                void reviewPresence.focusEvidenceDrawerStore(storeId);
            }
        }
        // `superseded` means this saved decision replaced a prior one, not that it is stale.
        acknowledgeEvidenceReview(queryClient, queryKey, postingEventId, decision, feedbackText, retailerReady);
        scheduleReviewRefresh(queryClient, [queryKey, ["tracker-stores"], ["/api/review/pending"], ["/api/review/submissions", postingEventId]]);
    }

    function handleAdminNotesSaved() {
        queryClient.invalidateQueries({ queryKey });
    }

    async function promoteToRetailerReadyByEvent(postingEventId: number) {
        const lookupRes = await apiFetch(`/api/review/submissions/${postingEventId}`, {
            headers: { "Content-Type": "application/json" },
        });
        if (!lookupRes.ok) throw new Error("Could not load submission details");
        const lookupData = await lookupRes.json();
        const decisionId = lookupData?.data?.latestDecision?.id;
        if (!decisionId) throw new Error("No decision found to promote");

        const res = await apiFetch(`/api/review/decisions/${decisionId}/promote-retailer-ready`, {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
        });
        if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            throw new Error(err.error || `Promote failed (${res.status})`);
        }
        await queryClient.invalidateQueries({ queryKey });
        await queryClient.invalidateQueries({ queryKey: ["tracker-stores"] });
    }

    async function handleApproveAll() {
        if (!effectiveData) return;
        const pending = filterTrackerTasksByReview(allTasks, 'unreviewed');
        if (pending.length === 0) return;
        const scope = reviewScope;
        setApprovingAll(true);
        setBulkReviewError(null);
        try {
            const results = await Promise.allSettled(pending.map(t => submitDecision(t.postingEventId, "APPROVE")));
            const failures = results.filter((result): result is PromiseRejectedResult => result.status === "rejected");
            if (failures.length) {
                const firstError = failures[0].reason;
                const reason = firstError instanceof Error ? firstError.message : "The request failed.";
                throw new Error(`${failures.length} of ${pending.length} approvals failed. ${reason}${/[.!?]$/.test(reason) ? "" : "."} Saved decisions are kept. Review the error before retrying remaining unreviewed submissions.`);
            }
        } catch (err) {
            // Network/concurrency failures must not surface as unhandled rejections;
            // a granular per-row retry happens via the inline ReviewControls.
            if (currentReviewScope.current === scope) setBulkReviewError(err instanceof Error ? err.message : 'Bulk approval failed.');
            console.error('[EvidenceDrawer] Approve-all partially failed', err);
        } finally {
            setApprovingAll(false);
        }
    }

    async function handleRejectAll(rejectionType: RejectionType, feedbackText: string, reasonCodes: string[]) {
        if (!effectiveData) return;
        const pending = filterTrackerTasksByReview(allTasks, 'unreviewed');
        if (pending.length === 0) return;
        const scope = reviewScope;
        setRejectingAll(true);
        setBulkReviewError(null);
        try {
            const results = await Promise.allSettled(
                pending.map((task) => submitDecision(task.postingEventId, "REJECT", rejectionType, feedbackText, reasonCodes))
            );
            const failures = results.filter((result): result is PromiseRejectedResult => result.status === "rejected");
            if (failures.length) {
                const firstError = failures[0].reason;
                const reason = firstError instanceof Error ? firstError.message : "The request failed.";
                throw new Error(`${failures.length} of ${pending.length} rejections failed. ${reason}${/[.!?]$/.test(reason) ? "" : "."} Saved decisions are kept. Review the error before retrying remaining unreviewed submissions.`);
            }
            setRejectAllOpen(false);
        } catch (err) {
            if (currentReviewScope.current === scope) setBulkReviewError(err instanceof Error ? err.message : 'Bulk rejection failed.');
            console.error('[EvidenceDrawer] Reject-all partially failed', err);
        } finally {
            setRejectingAll(false);
        }
    }

    const handleCorrectionSaved = useCallback(async () => {
        await queryClient.invalidateQueries({ queryKey });
        await queryClient.invalidateQueries({ queryKey: ["tracker-stores"] });
    }, [queryClient, queryKey]);

    const totalPhotos = flatPhotos.length;
    const lightboxOpen = lightboxIndex !== null;

    return (
        <>
            <Drawer open={open} onOpenChange={onOpenChange}>
                <DrawerContent className="max-h-[90vh]">
                    <DrawerHeader>
                        <DrawerTitle>{effectiveData?.storeName || "Loading..."}</DrawerTitle>
                        <DrawerDescription>
                            {effectiveData?.operatorName && `Operator: ${effectiveData.operatorName}`}
                            {effectiveData?.checkInAt && ` • In: ${formatTrackerVisitDateTime(effectiveData.checkInAt)}`}
                            {effectiveData?.checkOutAt && ` • Out: ${formatTrackerVisitDateTime(effectiveData.checkOutAt)}`}
                            {totalPhotos > 0 && ` • ${totalPhotos} photo${totalPhotos !== 1 ? "s" : ""}`}
                        </DrawerDescription>
                    </DrawerHeader>

                    <div className="px-4 pb-4 overflow-auto">
                        {isLoading ? (
                            <div className="flex items-center justify-center py-8">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
                            </div>
                        ) : effectiveData ? (
                            <div className="space-y-4">
                                {effectiveData.lastSyncAt && (
                                    <div className="text-xs text-muted-foreground">
                                        Last sync: {formatDistanceToNow(new Date(effectiveData.lastSyncAt), { addSuffix: true })}
                                    </div>
                                )}

                                {effectiveData.reviewSummary && effectiveData.reviewSummary.total > 0 && (
                                    <ReviewProgressBar
                                        summary={effectiveData.reviewSummary}
                                        onApproveAll={handleApproveAll}
                                        onOpenRejectAll={() => setRejectAllOpen(true)}
                                        onRejectAll={handleRejectAll}
                                        onRejectAllCancel={() => setRejectAllOpen(false)}
                                        approvingAll={approvingAll}
                                        rejectAllOpen={rejectAllOpen}
                                        rejectingAll={rejectingAll}
                                    />
                                )}

                                {bulkReviewError && <p role="alert" className="text-sm text-red-700">{bulkReviewError}</p>}
                                <ReviewFilterBar
                                    value={reviewFilter}
                                    counts={reviewFilterCounts}
                                    onChange={(nextFilter) => {
                                        setReviewFilter(nextFilter);
                                        setLightboxIndex(null);
                                    }}
                                />

                                {effectiveData.issues.length > 0 && (
                                    <div className="space-y-2">
                                        <div className="text-sm font-medium text-orange-600 flex items-center gap-2">
                                            <AlertTriangle className="h-4 w-4" /> Issues ({effectiveData.issues.length})
                                        </div>
                                        {effectiveData.issues.map(issue => (
                                            <div key={issue.issueId} className="border border-orange-200 bg-orange-50 rounded-lg p-3">
                                                <div className="text-sm font-medium">{issue.type}</div>
                                                <div className="text-xs text-gray-600">{issue.description}</div>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                <Separator />

                                {filteredPostings.length > 0 && (
                                    <div className="space-y-3">
                                        <div className="text-sm font-medium flex items-center gap-2">
                                            <Package className="h-4 w-4 text-blue-500" />
                                            Postings ({filteredPostings.length})
                                        </div>
                                        {filteredPostings.map((task, idx) => (
                                            <TaskCard key={idx} task={task} flatPhotos={flatPhotos} onPhotoClick={setLightboxIndex} onDecision={submitDecision} onPromoteRetailerReady={promoteToRetailerReadyByEvent} onCorrectionSaved={handleCorrectionSaved} onAdminNotesSaved={handleAdminNotesSaved} activeRetailerStandards={effectiveData.activeRetailerStandards} />
                                        ))}
                                    </div>
                                )}

                                {filteredRemovals.length > 0 && (
                                    <div className="space-y-3">
                                        <div className="text-sm font-medium flex items-center gap-2">
                                            <Trash2 className="h-4 w-4 text-orange-500" />
                                            Removals ({filteredRemovals.length})
                                        </div>
                                        {filteredRemovals.map((task, idx) => (
                                            <TaskCard key={idx} task={task} flatPhotos={flatPhotos} onPhotoClick={setLightboxIndex} onDecision={submitDecision} onPromoteRetailerReady={promoteToRetailerReadyByEvent} onCorrectionSaved={handleCorrectionSaved} onAdminNotesSaved={handleAdminNotesSaved} activeRetailerStandards={effectiveData.activeRetailerStandards} />
                                        ))}
                                    </div>
                                )}

                                {visibleTasks.length === 0 && (
                                    <div className="text-sm text-muted-foreground italic">
                                        {reviewFilter === "all" ? "No tasks recorded" : `No ${reviewFilter === "unreviewed" ? "unreviewed" : reviewFilter} tasks in this store.`}
                                    </div>
                                )}

                                {effectiveData.managerSignature && (
                                    <>
                                        <Separator />
                                        <ManagerSignatureBlock sig={effectiveData.managerSignature} />
                                    </>
                                )}

                                {(effectiveData.verificationContext.gpsAccuracyMeters !== null || effectiveData.verificationContext.distanceToStoreMeters !== null) && (
                                    <>
                                        <Separator />
                                        <div className="space-y-2">
                                            <div className="text-sm font-medium">Verification</div>
                                            <div className="grid grid-cols-2 gap-2 text-xs">
                                                {effectiveData.verificationContext.gpsAccuracyMeters !== null && (
                                                    <div><span className="text-muted-foreground">GPS Accuracy:</span> {effectiveData.verificationContext.gpsAccuracyMeters}m</div>
                                                )}
                                                {effectiveData.verificationContext.distanceToStoreMeters !== null && (
                                                    <div><span className="text-muted-foreground">Distance to store:</span> {effectiveData.verificationContext.distanceToStoreMeters}m</div>
                                                )}
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                        ) : (
                            <div className="text-center py-8 text-muted-foreground">Failed to load evidence data</div>
                        )}
                    </div>

                    <DrawerFooter>
                        <DrawerClose asChild>
                            <Button variant="outline">Close</Button>
                        </DrawerClose>
                    </DrawerFooter>

                    {/* Lightbox renders inside DrawerContent so Vaul's pointer handlers don't intercept clicks */}
                    {lightboxOpen && flatPhotos.length > 0 && (
                        <PhotoLightbox
                            flatPhotos={flatPhotos}
                            allTasks={visibleTasks}
                            initialIndex={lightboxIndex!}
                            storeName={effectiveData?.storeName}
                            onClose={() => setLightboxIndex(null)}
                            onDecision={submitDecision}
                            onPromoteRetailerReady={promoteToRetailerReadyByEvent}
                            activeRetailerStandards={effectiveData?.activeRetailerStandards}
                        />
                    )}
                </DrawerContent>
            </Drawer>
        </>
    );
}

export default EvidenceDrawer;
